import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';
import logo from './logo.svg';
import { BrowserRouter as Router, Routes, Route, useParams, Link } from 'react-router-dom';

// ==================== TOAST CONTEXT ====================
const ToastContext = React.createContext();

const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const showToast = useCallback((message, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 4000);
  }, []);
  return (
    <ToastContext.Provider value={showToast}>
      {children}
      <div className="toast-container">
        {toasts.map(toast => (
          <div key={toast.id} className={`toast toast-${toast.type}`}>
            <span className="toast-icon">{toast.type === 'success' ? '✓' : '✕'}</span>
            <span className="toast-message">{toast.message}</span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

const useToast = () => React.useContext(ToastContext);

// ==================== VALIDATION HELPERS ====================
const isValidEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!re.test(email)) return false;
  const domain = email.split('@')[1].toLowerCase();
  const blocked = ['test.com', 'example.com', 'localhost', 'domain.com', 'email.com', 'mailinator.com',
    'temp-mail.org', 'throwaway.com', 'yopmail.com', 'guerrillamail.com'];
  if (blocked.includes(domain)) return false;
  const tld = domain.split('.').pop();
  return tld.length >= 2;
};

const isValidPhone = (phone) => {
  const digits = phone.replace(/\D/g, '');
  if (digits.startsWith('03') && digits.length === 11) return true;
  if (digits.startsWith('92') && digits.length === 12) return true;
  if (phone.startsWith('+92') && digits.length === 12) return true;
  if (phone.startsWith('0092') && digits.length === 13) return true;
  return false;
};

// ==================== ANIMATED STEPS COMPONENT ====================
const AnimatedSteps = ({ steps, title, icon, color }) => {
  const [activeStep, setActiveStep] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveStep(prev => (prev + 1) % steps.length);
    }, 2200);
    return () => clearInterval(timer);
  }, [steps.length]);

  return (
    <div className="animated-steps-card">
      <div className="steps-card-header" style={{ background: color }}>
        <span className="steps-card-icon">{icon}</span>
        <h3>{title}</h3>
      </div>
      <div className="steps-list">
        {steps.map((step, i) => (
          <div
            key={i}
            className={`step-item ${activeStep === i ? 'step-active' : ''} ${i < activeStep ? 'step-done' : ''}`}
            onClick={() => setActiveStep(i)}
          >
            <div className="step-number-wrap">
              <div className="step-number">
                {i < activeStep ? <span className="step-check">✓</span> : i + 1}
              </div>
              {i < steps.length - 1 && <div className="step-connector"></div>}
            </div>
            <div className="step-content">
              <div className="step-icon-mini">{step.icon}</div>
              <div className="step-text">
                <strong>{step.title}</strong>
                <p>{step.desc}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ==================== INSTALL PAGE ====================
const InstallPage = () => {
  const { token } = useParams();
  const [email, setEmail] = useState('');
  const [verified, setVerified] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const showToast = useToast();
  const API_BASE = 'https://copenterbackend.vercel.app';

  const mobileSteps = [
    { icon: '🌐', title: 'Install Kiwi Browser', desc: 'Download Kiwi Browser .apk from Chrome or Play Store' },
    { icon: '📲', title: 'Open Kiwi Browser', desc: 'Launch the browser on your Android device' },
    { icon: '☰', title: 'Tap Three Lines', desc: 'Tap the three horizontal lines at the top-right corner' },
    { icon: '🧩', title: 'Go to Extensions', desc: 'Select "Extensions" from the menu that opens' },
    { icon: '⚙️', title: 'Enable Developer Mode', desc: 'Toggle ON Developer mode at the top of Extensions page' },
    { icon: '📂', title: 'Load .zip File', desc: 'Tap "+ (from .crx/.zip)" and choose your downloaded file' },
    { icon: '✅', title: 'Pin Extension', desc: 'Go to EMTTS website, tap ☰, click extension & press Start!' },
  ];

  const desktopSteps = [
    { icon: '🌐', title: 'Open Chrome or Edge', desc: 'Launch your preferred desktop browser' },
    { icon: '⋮', title: 'Click Three Dots Menu', desc: 'Click ⋮ at the top-right corner of the browser' },
    { icon: '🧩', title: 'Go to Extensions', desc: 'Navigate to More Tools → Extensions' },
    { icon: '⚙️', title: 'Enable Developer Mode', desc: 'Toggle Developer mode ON (top-right of page)' },
    { icon: '📁', title: 'Load .crx/.zip File', desc: 'Drag and drop the .crx file onto the Extensions page' },
    { icon: '✅', title: 'Confirm Installation', desc: 'Click "Add Extension" in the popup dialog' },
    { icon: '📌', title: 'Pin & Use', desc: 'Pin from puzzle icon, visit EMTTS, click extension & Start!' },
  ];

  const handleVerify = async () => {
    if (!email.trim()) { setError('Email is required.'); return; }
    if (!isValidEmail(email)) { setError('Enter a valid email.'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_BASE}/api/verify-token`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, email })
      });
      const data = await res.json();
      if (data.success) {
        setVerified(true);
        setDownloadUrl(`${API_BASE}/api/download/extension/${token}`);
        showToast('Email verified successfully!', 'success');
      } else {
        setError(data.message || 'Verification failed');
        showToast(data.message || 'Verification failed', 'error');
      }
    } catch {
      setError('Network error'); showToast('Network error', 'error');
    } finally { setLoading(false); }
  };

  return (
    <div className="install-page">
      <div className="install-bg-orb orb1"></div>
      <div className="install-bg-orb orb2"></div>
      <div className="install-container">
        <header className="install-header">
          <div className="install-logo-wrap">
            <img src={logo} alt="logo" height="48" />
            <span className="install-logo-text">CopEnter Pro</span>
          </div>
          <h1 className="install-title">Install Your Extension</h1>
          <p className="install-subtitle">Verify your email to unlock the download</p>
        </header>

        <div className="verify-card glass-card">
          <div className="verify-card-icon">🔐</div>
          <h2>Email Verification</h2>
          <p className="verify-desc">Enter the email address you used during registration.</p>
          <div className="verify-group">
            <div className="input-icon-wrap">
              <span className="input-icon">✉️</span>
              <input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(''); }}
                disabled={verified}
                className="glass-input"
              />
            </div>
            {!verified && (
              <button onClick={handleVerify} disabled={loading} className="glass-btn-primary verify-btn">
                {loading ? <span className="spinner"></span> : <><span>🔓</span> Verify & Unlock</>}
              </button>
            )}
          </div>
          {error && <div className="error-message"><span>⚠️</span> {error}</div>}
          {verified && (
            <div className="download-success">
              <div className="success-check-anim">✓</div>
              <h3>Email Verified!</h3>
              <a href={downloadUrl} className="download-big-btn" download>
                <span className="dl-icon">⬇️</span>
                Download Extension (.crx)
              </a>
              <p className="note">Follow the guide below to install on your device.</p>
            </div>
          )}
        </div>

        <div className="install-guides-section">
          <h2 className="guides-title">📋 Installation Guide</h2>
          <div className="guides-grid">
            <AnimatedSteps
              steps={mobileSteps}
              title="Mobile (Kiwi Browser)"
              icon="📱"
              color="linear-gradient(135deg, #ff6600, #ff8533)"
            />
            <AnimatedSteps
              steps={desktopSteps}
              title="Desktop (Chrome / Edge)"
              icon="💻"
              color="linear-gradient(135deg, #1a1a2e, #16213e)"
            />
          </div>
        </div>

        <div className="usage-video-section glass-card">
          <h2>🎬 How to Use CopEnter Pro</h2>
          <p>Watch the step-by-step visual guide to start automating EMTTS entries</p>
          <a href="/guide.html" target="_blank" className="watch-guide-btn">
            <span>▶</span> Watch Visual Guide
          </a>
        </div>

        <footer className="install-footer">
          <p>© 2026 CopEnter Pro · Built with precision</p>
        </footer>
      </div>
    </div>
  );
};

// ==================== FEATURE CARD ====================
const FeatureCard = ({ icon, title, desc, delay }) => (
  <div className="feature-card glass-card" style={{ animationDelay: `${delay}s` }}>
    <div className="feature-icon">{icon}</div>
    <h3>{title}</h3>
    <p>{desc}</p>
  </div>
);

// ==================== HOME PAGE ====================
const HomePage = () => {
  const showToast = useToast();
  const [user, setUser] = useState({ name: '', email: '', phone: '' });
  const [isRegistered, setIsRegistered] = useState(false);
  const [isApproved, setIsApproved] = useState(false);
  const [downloadToken, setDownloadToken] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [formError, setFormError] = useState('');
  const [showInstallGuide, setShowInstallGuide] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const chatEndRef = useRef(null);
  const API_BASE_URL = "https://copenterbackend.vercel.app";

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (!isRegistered || !user.email) return;
    const checkApproval = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/user/token/${user.email}`);
        const data = await res.json();
        if (data.success && data.isApproved && data.token) {
          setIsApproved(true); setDownloadToken(data.token);
          showToast('🎉 Account approved! You can now download.', 'success');
        } else if (data.isApproved === true && !data.token) {
          setIsApproved(true);
        }
      } catch {}
    };
    checkApproval();
    const interval = setInterval(checkApproval, 5000);
    return () => clearInterval(interval);
  }, [isRegistered, user.email, showToast]);

  useEffect(() => {
    if (!isRegistered || !user.email) return;
    const loadMessages = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/chat/messages/${user.email}`);
        const data = await res.json();
        if (Array.isArray(data)) setMessages(data);
      } catch {}
    };
    loadMessages();
    const interval = setInterval(loadMessages, 8000);
    return () => clearInterval(interval);
  }, [isRegistered, user.email]);

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    setIsSending(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/chat/send`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userName: user.name, userEmail: user.email, message: newMessage })
      });
      const data = await res.json();
      if (data.success) {
        setNewMessage('');
        const res2 = await fetch(`${API_BASE_URL}/api/chat/messages/${user.email}`);
        const data2 = await res2.json();
        if (Array.isArray(data2)) setMessages(data2);
        showToast('Message sent!', 'success');
      } else { showToast('Failed to send', 'error'); }
    } catch { showToast('Connection error', 'error'); }
    finally { setIsSending(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!user.name.trim()) { setFormError('Full name is required.'); return; }
    if (!isValidEmail(user.email)) { setFormError('Enter a real email address.'); return; }
    if (!isValidPhone(user.phone)) { setFormError('Phone must be 11 digits starting with 03 or +92.'); return; }
    setIsLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/register`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(user)
      });
      const data = await res.json();
      if (data.success) {
        setIsRegistered(true);
        showToast('✅ Registered! Awaiting approval.', 'success');
      } else { setFormError(data.message || 'Registration failed'); }
    } catch { setFormError('Network error'); }
    finally { setIsLoading(false); }
  };

  const handleCheckStatus = async () => {
    if (!user.email) { showToast('Enter your email first', 'error'); return; }
    setIsLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/check-status/${user.email}`);
      const statusData = await res.json();
      if (statusData.active === true) {
        const tokenRes = await fetch(`${API_BASE_URL}/api/user/token/${user.email}`);
        const tokenData = await tokenRes.json();
        if (tokenData.success && tokenData.token) {
          setIsApproved(true); setDownloadToken(tokenData.token);
          showToast('🎉 Active! Download ready.', 'success');
          setShowInstallGuide(true);
        } else {
          setIsApproved(true);
          showToast('Approved but token missing. Contact admin.', 'error');
        }
      } else {
        showToast('⏳ Still pending approval.', 'error');
      }
    } catch { showToast('Error checking status', 'error'); }
    finally { setIsLoading(false); }
  };

  const handleDownload = () => {
    if (downloadToken) window.open(`${API_BASE_URL}/api/download/extension/${downloadToken}`, '_blank');
    else showToast('Download token not available.', 'error');
  };

  const mobileSteps = [
    { icon: '🌐', title: 'Install Kiwi Browser', desc: 'Download Kiwi Browser .apk file from Chrome search or Play Store' },
    { icon: '📲', title: 'Open Kiwi Browser', desc: 'Launch Kiwi and browse normally' },
    { icon: '☰', title: 'Tap Three Lines (≡)', desc: 'Tap the three horizontal lines at the top-right corner' },
    { icon: '🧩', title: 'Choose Extensions', desc: 'Select "Extensions" from the dropdown menu' },
    { icon: '📂', title: 'Select from .crx', desc: 'Tap "+ (from .crx/.zip)" option and select your downloaded file' },
    { icon: '✅', title: 'Choose Your Extension', desc: 'Your CopEnter extension (downloaded after approval) will appear — tap it' },
    { icon: '🚀', title: 'Go to EMTTS & Start', desc: 'Open EMTTS website, tap ☰, click extension, paste articles, press Start!' },
  ];

  const desktopSteps = [
    { icon: '🌐', title: 'Open Chrome or Edge', desc: 'Launch your desktop browser' },
    { icon: '⋮', title: 'Click Three Dots (⋮)', desc: 'Click menu at top-right corner' },
    { icon: '🧩', title: 'Go to Extensions', desc: 'More Tools → Extensions' },
    { icon: '⚙️', title: 'Enable Developer Mode', desc: 'Toggle ON at top-right of Extensions page' },
    { icon: '📁', title: 'Drag .crx File', desc: 'Drag & drop the .crx file downloaded from CopEnter onto the page' },
    { icon: '✅', title: 'Add Extension', desc: 'Click "Add Extension" in popup' },
    { icon: '🚀', title: 'Pin & Use', desc: 'Pin from puzzle icon, go to EMTTS, click extension in toolbar & Start!' },
  ];

  return (
    <div className="homepage">
      {/* ======= NAVBAR ======= */}
      <nav className={`navbar glass-nav ${scrollY > 60 ? 'navbar-scrolled' : ''}`}>
        <div className="nav-logo">
          <img src={logo} alt="logo" height="36" />
          <span>CopEnter <em>Pro</em></span>
        </div>
        <div className="nav-links">
          <Link to="/about">About</Link>
          <Link to="/privacy">Privacy</Link>
          <Link to="/contact">Contact</Link>
          <a href="/guide.html" target="_blank" className="nav-cta-link">📺 Guide</a>
        </div>
        <button
          className="mobile-register-btn"
          onClick={() => document.getElementById('register-section').scrollIntoView({ behavior: 'smooth' })}
        >
          Get Started
        </button>
      </nav>

      {/* ======= HERO ======= */}
      <header className="hero">
        <div className="hero-bg-circle c1"></div>
        <div className="hero-bg-circle c2"></div>
        <div className="hero-bg-circle c3"></div>
        <div className="hero-content">
          <div className="hero-badge"> EMTTS Automation Tool</div>
          <h1>
            Automate <span className="hero-highlight">EMTTS</span><br />
            Effortlessly
          </h1>
          <p>The fastest browser extension to enter data. Install once, save hours every day. Works on Mobile & Desktop.</p>
          <div className="hero-btns">
            <button className="cta-btn" onClick={() => document.getElementById('register-section').scrollIntoView({ behavior: 'smooth' })}>
              🎯 Get Started Free
            </button>
            <a href="/guide.html" target="_blank" className="cta-btn-outline">
              ▶ Watch Guide
            </a>
          </div>
          <div className="hero-stats">
            <div className="hero-stat"><span>⚡</span><strong>10x</strong><small>Faster</small></div>
            <div className="hero-stat-divider"></div>
            <div className="hero-stat"><span>📱</span><strong>Mobile</strong><small>Ready</small></div>
            <div className="hero-stat-divider"></div>
            <div className="hero-stat"><span>🔒</span><strong>Secure</strong><small>Access</small></div>
          </div>
        </div>
      </header>

      {/* ======= FEATURES ======= */}
      <section className="features-section">
        <div className="section-label">WHY COPENTER PRO</div>
        <h2 className="section-title">Everything You Need</h2>
        <div className="features-grid">
          <FeatureCard icon="⚡" title="Lightning Fast" desc="Automated EMTTS entry saves you hours of manual work daily." delay={0} />
          <FeatureCard icon="📱" title="Mobile Ready" desc="Works on Android via Kiwi Browser. Full mobile support." delay={0.1} />
          <FeatureCard icon="🔒" title="Secure License" desc="Token-based activation. Your account stays protected." delay={0.2} />
          <FeatureCard icon="💬" title="Live Support" desc="Chat with admin directly while waiting for approval." delay={0.3} />
          <FeatureCard icon="🖥️" title="Desktop Support" desc="Chrome & Edge compatible. Install in seconds." delay={0.4} />
          <FeatureCard icon="✅" title="Easy Setup" desc="Follow our step-by-step guide. No technical skills needed." delay={0.5} />
        </div>
      </section>

      {/* ======= REGISTER SECTION ======= */}
      <section id="register-section" className="register-section">
        <div className="register-panel glass-card">
          {!isRegistered ? (
            <>
              <div className="register-header">
                <div className="register-icon-big">👤</div>
                <h2>Create Your Account</h2>
                <p className="panel-subtitle">Fill in your details to request activation</p>
              </div>
              {formError && <div className="error-message"><span>⚠️</span> {formError}</div>}
              <form onSubmit={handleRegister} className="register-form">
                <div className="field-group">
                  <label><span>👤</span> Full Name</label>
                  <div className="input-wrap">
                    <input
                      type="text"
                      placeholder="Enter your full name"
                      value={user.name}
                      onChange={e => { setUser({...user, name: e.target.value}); setFormError(''); }}
                      className="glass-input"
                    />
                  </div>
                </div>
                <div className="field-group">
                  <label><span>✉️</span> Email Address</label>
                  <div className="input-wrap">
                    <input
                      type="email"
                      placeholder="you@example.com"
                      value={user.email}
                      onChange={e => { setUser({...user, email: e.target.value}); setFormError(''); }}
                      className="glass-input"
                    />
                  </div>
                </div>
                <div className="field-group">
                  <label>
                    <span>📱</span> WhatsApp Number
                    <small className="optional-tag"></small>
                  </label>
                  <div className="input-wrap">
                    <input
                      type="text"
                      placeholder="03........."
                      value={user.phone}
                      onChange={e => { setUser({...user, phone: e.target.value}); setFormError(''); }}
                      className="glass-input"
                    />
                  </div>
                  <small className="field-hint"></small>
                </div>
                <button type="submit" disabled={isLoading} className="glass-btn-primary submit-btn">
                  {isLoading ? <><span className="spinner"></span> Registering...</> : <><span>🚀</span> Register & Request Activation</>}
                </button>
              </form>
            </>
          ) : (
            <div className="status-area">
              <div className={`status-badge-big ${isApproved ? 'active' : 'pending'}`}>
                {isApproved ? '✅ Active' : '⏳ Pending Approval'}
              </div>
              {!isApproved ? (
                <div className="pending-area">
                  <div className="pending-animation">
                    <div className="pending-dot"></div>
                    <div className="pending-dot"></div>
                    <div className="pending-dot"></div>
                  </div>
                  <p className="status-text">Your account is awaiting admin verification.<br />You'll be notified once approved.</p>
                  <div className="status-actions">
                    <button onClick={handleCheckStatus} disabled={isLoading} className="glass-btn-primary check-btn">
                      {isLoading ? <><span className="spinner"></span> Checking...</> : <><span>🔍</span> Check My Status</>}
                    </button>
                    <button onClick={() => setShowChat(!showChat)} className="glass-btn-secondary chat-toggle-btn">
                      {showChat ? '✕ Close Chat' : '💬 Open Support Chat'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="approved-area">
                  <div className="approved-animation">🎉</div>
                  <p className="approved-text">Your account is active and ready!</p>
                  <button onClick={handleDownload} className="glass-btn-primary download-btn-green">
                    <span>⬇️</span> Download Extension (.crx)
                  </button>
                  <button className="glass-btn-text guide-toggle" onClick={() => setShowInstallGuide(!showInstallGuide)}>
                    {showInstallGuide ? '▲ Hide Installation Guide' : '▼ Show Installation Guide'}
                  </button>
                </div>
              )}

              {/* ===== MINI INSTALL GUIDE ===== */}
              {showInstallGuide && isApproved && (
                <div className="install-guide-mini">
                  <h3 className="guide-mini-title">📋 Installation Guide</h3>
                  <div className="guide-mini-grid">
                    <AnimatedSteps
                      steps={mobileSteps}
                      title="Mobile (Kiwi)"
                      icon="📱"
                      color="linear-gradient(135deg,#ff6600,#ff8533)"
                    />
                    <AnimatedSteps
                      steps={desktopSteps}
                      title="Desktop (Chrome)"
                      icon="💻"
                      color="linear-gradient(135deg,#1a1a2e,#333)"
                    />
                  </div>
                </div>
              )}

              {/* ===== CHAT WIDGET ===== */}
              {showChat && !isApproved && (
                <div className="chat-widget glass-card">
                  <div className="chat-header">
                    <div className="chat-header-left">
                      <span className="chat-avatar">👤</span>
                      <div>
                        <strong>Support Chat</strong>
                        <small>CopEnter Admin</small>
                      </div>
                    </div>
                    <div className="chat-status">
                      <span className="online-indicator"></span>
                      <span>Online</span>
                    </div>
                  </div>
                  <div className="chat-messages">
                    {messages.length === 0 && (
                      <div className="chat-empty">
                        <span>💬</span>
                        <p>No messages yet. Ask us anything!</p>
                      </div>
                    )}
                    {messages.map((msg, i) => (
                      <div key={i} className={`chat-bubble-wrap ${msg.isAdminReply ? 'admin-wrap' : 'user-wrap'}`}>
                        <div className={`chat-bubble ${msg.isAdminReply ? 'admin' : 'user'}`}>
                          <div className="bubble-sender">{msg.isAdminReply ? '👤 Admin' : '🙋 You'}</div>
                          <p>{msg.message}</p>
                          {msg.adminReply && (
                            <div className="reply-bubble">
                              <span>↩️</span> {msg.adminReply}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    <div ref={chatEndRef} />
                  </div>
                  <div className="chat-input-area">
                    <textarea
                      value={newMessage}
                      onChange={e => setNewMessage(e.target.value)}
                      placeholder="Type your message..."
                      rows="2"
                      disabled={isSending}
                      className="chat-textarea glass-input"
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                    />
                    <button onClick={sendMessage} disabled={isSending} className="chat-send-btn">
                      {isSending ? <span className="spinner"></span> : '➤'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* ======= HOW IT WORKS (GUIDE LINK) ======= */}
      <section className="guide-section">
        <div className="guide-cta glass-card">
          <div className="guide-cta-icon">🎬</div>
          <div className="guide-cta-text">
            <h2>See How It Works</h2>
            <p>Watch our visual step-by-step guide for mobile and desktop installation</p>
          </div>
          <a href="/guide.html" target="_blank" className="watch-guide-btn">
            ▶ Watch Visual Guide
          </a>
        </div>
      </section>

      {/* ======= FOOTER ======= */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-top">
            <div className="footer-brand-col">
              <div className="footer-brand">
                <img src={logo} alt="logo" height="32" />
                <span>CopEnter <em>Pro</em></span>
              </div>
              <p className="footer-brand-desc">The fastest EMTTS automation tool for mobile and desktop. Save hours daily.</p>
            </div>
            <div className="footer-links-col">
              <h4>Pages</h4>
              <Link to="/about">About</Link>
              <Link to="/privacy">Privacy Policy</Link>
              <Link to="/contact">Contact Us</Link>
              <a href="/guide.html" target="_blank">Visual Guide</a>
            </div>
            <div className="footer-contact-col">
              <h4>Support</h4>
              <p>📧 Register an account and use the live chat for support.</p>
              <p>📱 Pakistan numbers supported (03xx / +92xxx)</p>
            </div>
          </div>
          <div className="footer-bottom">
            <p>© 2026 CopEnter Pro · All rights reserved · Built with precision</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

// ==================== ABOUT PAGE ====================
const AboutPage = () => (
  <div className="static-page-wrap">
    <nav className="navbar glass-nav">
      <div className="nav-logo">
        <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--primary-orange)', fontWeight: 800 }}>
          ← CopEnter Pro
        </Link>
      </div>
    </nav>
    <div className="static-page glass-card">
      <div className="static-page-icon">ℹ️</div>
      <h2>About CopEnter Pro</h2>
      <p>CopEnter Pro is a secure and efficient EMTTS automation browser extension. It helps users enter data faster on the EMTTS platform — working seamlessly on both mobile (via Kiwi Browser) and desktop (Chrome / Edge).</p>
      <p>No data is ever stored on external servers. Your license email is used solely for verification and activation. The extension runs entirely in your browser, locally.</p>
      <div className="static-features-grid">
        <div className="static-feature-card"><span>⚡</span><strong>Fast Entry</strong><p>Automates repetitive EMTTS data entry in seconds.</p></div>
        <div className="static-feature-card"><span>🔒</span><strong>Secure</strong><p>Token-based access. Only approved users can download.</p></div>
        <div className="static-feature-card"><span>📱</span><strong>Mobile Ready</strong><p>Works on Android with Kiwi Browser.</p></div>
        <div className="static-feature-card"><span>💻</span><strong>Desktop Support</strong><p>Chrome and Edge compatible.</p></div>
      </div>
    </div>
  </div>
);

// ==================== PRIVACY PAGE ====================
const PrivacyPage = () => (
  <div className="static-page-wrap">
    <nav className="navbar glass-nav">
      <div className="nav-logo">
        <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--primary-orange)', fontWeight: 800 }}>
          ← CopEnter Pro
        </Link>
      </div>
    </nav>
    <div className="static-page glass-card">
      <div className="static-page-icon">🔒</div>
      <h2>Privacy Policy</h2>
      <p>Your privacy is our top priority. CopEnter Pro collects only the minimum information needed to verify and activate your license.</p>
      <div className="privacy-points">
        <div className="privacy-point"><span>✅</span><p>The extension runs <strong>entirely locally</strong> in your browser.</p></div>
        <div className="privacy-point"><span>✅</span><p>Only your <strong>email address</strong> is used for verification.</p></div>
        <div className="privacy-point"><span>✅</span><p>We <strong>never share</strong> your information with third parties.</p></div>
        <div className="privacy-point"><span>✅</span><p>No tracking, no analytics on your browsing behavior.</p></div>
        <div className="privacy-point"><span>✅</span><p>Your WhatsApp number is used <strong>only for support contact</strong> if needed.</p></div>
      </div>
      <p>If you have any privacy concerns, contact us through the Contact page.</p>
    </div>
  </div>
);

// ==================== CONTACT PAGE ====================
const ContactPage = () => {
  const showToast = useToast();
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [sending, setSending] = useState(false);
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { showToast('Please fill all fields', 'error'); return; }
    setSending(true);
    setTimeout(() => {
      showToast('✅ Message sent successfully!', 'success');
      setForm({ name: '', email: '', message: '' });
      setSending(false);
    }, 1000);
  };
  return (
    <div className="static-page-wrap">
      <nav className="navbar glass-nav">
        <div className="nav-logo">
          <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--primary-orange)', fontWeight: 800 }}>
            ← CopEnter Pro
          </Link>
        </div>
      </nav>
      <div className="static-page glass-card">
        <div className="static-page-icon">✉️</div>
        <h2>Contact Us</h2>
        <p className="contact-subtitle">Have a question? Send us a message and we'll get back to you.</p>
        <form onSubmit={handleSubmit} className="contact-form">
          <div className="field-group">
            <label><span>👤</span> Your Name</label>
            <input type="text" placeholder="Full name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="glass-input" />
          </div>
          <div className="field-group">
            <label><span>✉️</span> Email Address</label>
            <input type="email" placeholder="you@example.com" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="glass-input" />
          </div>
          <div className="field-group">
            <label><span>💬</span> Message</label>
            <textarea placeholder="Your message..." rows="5" value={form.message} onChange={e => setForm({...form, message: e.target.value})} className="glass-input" />
          </div>
          <button type="submit" disabled={sending} className="glass-btn-primary">
            {sending ? <><span className="spinner"></span> Sending...</> : <><span>📤</span> Send Message</>}
          </button>
        </form>
      </div>
    </div>
  );
};

// ==================== APP ROUTER ====================
function App() {
  return (
    <Router>
      <ToastProvider>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/install/:token" element={<InstallPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/contact" element={<ContactPage />} />
        </Routes>
      </ToastProvider>
    </Router>
  );
}

export default App;
